import React, { useState, useMemo } from 'react';
import {
  Cloud, Activity, Check, AlertTriangle, X, RefreshCw,
  Clock, Zap, Database, Settings, ChevronRight, Search,
  Filter, TrendingUp, TrendingDown, Pause, Play, ExternalLink,
  Shield, Key, Server, Bell, GitBranch, Layers, TestTube, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AsyncButton } from '../components/ui/async-button';
import { toast } from 'sonner';
import { getLogoByIntegrationId } from '../constants/logos';

const mockIntegrations = [
  {
    id: 'int_001',
    logoId: 'okta',
    name: 'Okta',
    category: 'Identity',
    type: 'saas',
    status: 'healthy',
    lastSync: '2 mins ago',
    nextSync: 'in 13 mins',
    syncFrequency: '15m',
    actionsToday: 127,
    actionsWeek: 892,
    errorRate: 0.1,
    latency: '120ms',
    dataVolume: '2.4GB',
    permissions: ['users.read', 'users.write', 'groups.read', 'apps.read'],
    features: ['User sync', 'Group sync', 'MFA status', 'App assignments'],
    connected: true,
    connectedAt: '2025-10-15',
    icon: Key
  },
  {
    id: 'int_002',
    logoId: 'entra-id',
    name: 'Microsoft Entra ID',
    category: 'Identity',
    type: 'saas',
    status: 'healthy',
    lastSync: '5 mins ago',
    nextSync: 'in 10 mins',
    syncFrequency: '15m',
    actionsToday: 89,
    actionsWeek: 634,
    errorRate: 0.0,
    latency: '95ms',
    dataVolume: '1.8GB',
    permissions: ['User.Read.All', 'Group.Read.All', 'Directory.Read.All'],
    features: ['User sync', 'Group sync', 'Conditional access'],
    connected: true,
    connectedAt: '2025-09-20',
    icon: Shield
  },
  {
    id: 'int_003',
    logoId: 'jamf',
    name: 'Jamf Pro',
    category: 'Endpoint',
    type: 'saas',
    status: 'degraded',
    lastSync: '45 mins ago',
    nextSync: 'in 5 mins',
    syncFrequency: '30m',
    actionsToday: 34,
    actionsWeek: 245,
    errorRate: 2.3,
    latency: '450ms',
    dataVolume: '890MB',
    permissions: ['devices.read', 'devices.write', 'policies.read'],
    features: ['Device inventory', 'App deployment', 'Policy enforcement'],
    connected: true,
    connectedAt: '2025-11-01',
    icon: Server,
    alert: 'High latency detected - API response times above threshold'
  },
  {
    id: 'int_004',
    logoId: 'intune',
    name: 'Intune',
    category: 'Endpoint',
    type: 'saas',
    status: 'healthy',
    lastSync: '3 mins ago',
    nextSync: 'in 12 mins',
    syncFrequency: '15m',
    actionsToday: 56,
    actionsWeek: 398,
    errorRate: 0.2,
    latency: '110ms',
    dataVolume: '1.2GB',
    permissions: ['DeviceManagementManagedDevices.Read.All'],
    features: ['Device sync', 'Compliance status', 'App inventory'],
    connected: true,
    connectedAt: '2025-10-01',
    icon: Server
  },
  {
    id: 'int_005',
    logoId: 'google-workspace',
    name: 'Google Workspace',
    category: 'SaaS',
    type: 'saas',
    status: 'healthy',
    lastSync: '1 min ago',
    nextSync: 'in 14 mins',
    syncFrequency: '15m',
    actionsToday: 203,
    actionsWeek: 1456,
    errorRate: 0.05,
    latency: '80ms',
    dataVolume: '3.1GB',
    permissions: ['admin.directory.user', 'admin.directory.group'],
    features: ['User provisioning', 'Group sync', 'Drive permissions'],
    connected: true,
    connectedAt: '2025-08-15',
    icon: Cloud
  },
  {
    id: 'int_006',
    logoId: 'slack',
    name: 'Slack',
    category: 'SaaS',
    type: 'saas',
    status: 'error',
    lastSync: '2 hours ago',
    nextSync: 'pending reconnect',
    syncFrequency: '5m',
    actionsToday: 0,
    actionsWeek: 234,
    errorRate: 100,
    latency: '-',
    dataVolume: '450MB',
    permissions: ['users:read', 'channels:read', 'chat:write'],
    features: ['User sync', 'Channel notifications', 'Bot messages'],
    connected: true,
    connectedAt: '2025-09-10',
    icon: Bell,
    alert: 'OAuth token expired - reconnection required'
  },
  {
    id: 'int_007',
    logoId: 'github',
    name: 'GitHub',
    category: 'Development',
    type: 'saas',
    status: 'healthy',
    lastSync: '8 mins ago',
    nextSync: 'in 7 mins',
    syncFrequency: '15m',
    actionsToday: 45,
    actionsWeek: 312,
    errorRate: 0.0,
    latency: '65ms',
    dataVolume: '780MB',
    permissions: ['repo', 'read:org', 'read:user'],
    features: ['Repo access', 'Team sync', 'Audit logs'],
    connected: true,
    connectedAt: '2025-10-20',
    icon: GitBranch
  },
  {
    id: 'int_008',
    logoId: 'jira-sm',
    name: 'Jira',
    category: 'Operations',
    type: 'saas',
    status: 'paused',
    lastSync: '1 day ago',
    nextSync: 'paused',
    syncFrequency: '30m',
    actionsToday: 0,
    actionsWeek: 89,
    errorRate: 0.0,
    latency: '-',
    dataVolume: '560MB',
    permissions: ['read:jira-user', 'read:jira-work'],
    features: ['Issue sync', 'Project mapping', 'Workflow triggers'],
    connected: true,
    connectedAt: '2025-11-15',
    icon: Layers,
    paused: true,
    pausedReason: 'Scheduled maintenance window'
  },
  {
    id: 'int_009',
    logoId: 'aws',
    name: 'AWS',
    category: 'Infrastructure',
    type: 'cloud',
    status: 'healthy',
    lastSync: '1 min ago',
    nextSync: 'in 4 mins',
    syncFrequency: '5m',
    actionsToday: 156,
    actionsWeek: 1089,
    errorRate: 0.1,
    latency: '45ms',
    dataVolume: '5.2GB',
    permissions: ['iam:ListUsers', 'ec2:DescribeInstances', 's3:ListBuckets'],
    features: ['IAM sync', 'Resource inventory', 'Cost data'],
    connected: true,
    connectedAt: '2025-07-01',
    icon: Cloud
  },
  {
    id: 'int_010',
    logoId: 'datadog',
    name: 'Datadog',
    category: 'Operations',
    type: 'saas',
    status: 'healthy',
    lastSync: '30 secs ago',
    nextSync: 'in 30 secs',
    syncFrequency: '1m',
    actionsToday: 892,
    actionsWeek: 6234,
    errorRate: 0.0,
    latency: '35ms',
    dataVolume: '12.4GB',
    permissions: ['dashboards_read', 'monitors_read', 'logs_read'],
    features: ['Alert forwarding', 'Metric ingestion', 'Log correlation'],
    connected: true,
    connectedAt: '2025-08-01',
    icon: Activity
  }
];

const statusConfig = {
  healthy: { label: 'Healthy', className: 'bg-primary/10 text-primary', icon: Check },
  degraded: { label: 'Degraded', className: 'bg-slate-200 text-slate-700', icon: AlertTriangle },
  error: { label: 'Error', className: 'bg-slate-300 text-slate-800', icon: X },
  paused: { label: 'Paused', className: 'bg-slate-100 text-slate-500', icon: Pause }
};

const IntegrationDetailModal = ({ integration, onClose, onPause, onResume, onReconnect, onSyncNow, onTestConnection }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [isTesting, setIsTesting] = useState(false);

  if (!integration) return null;

  const StatusIcon = statusConfig[integration.status]?.icon || Check;
  const logo = integration.logoId ? getLogoByIntegrationId(integration.logoId) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-lg border border-slate-200 w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col">
        <div className="flex items-start justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              {logo ? (
                <img src={logo.src} alt={logo.alt} className="w-6 h-6 object-contain" />
              ) : (
                <integration.icon className="w-5 h-5 text-slate-600" />
              )}
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">{integration.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${statusConfig[integration.status]?.className}`}>
                  <StatusIcon className="w-3 h-3" />
                  {statusConfig[integration.status]?.label}
                </span>
                <span className="text-xs text-slate-500">{integration.category}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="flex items-center gap-1 px-4 pt-3 border-b border-slate-200">
          {['overview', 'permissions', 'activity', 'settings'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${activeTab === tab
                ? 'border-primary text-primary'
                : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'overview' && (
            <div className="space-y-4">
              {integration.alert && (
                <div className="bg-slate-100 border border-slate-300 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-slate-700 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm text-slate-700">{integration.alert}</p>
                    {integration.status === 'error' && (
                      <AsyncButton
                        size="sm"
                        variant="outline"
                        className="mt-2"
                        onClick={() => onReconnect(integration.id)}
                        loadingText="Reconnecting..."
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Reconnect
                      </AsyncButton>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Last Sync</div>
                  <div className="text-sm font-medium text-slate-900">{integration.lastSync}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Next Sync</div>
                  <div className="text-sm font-medium text-slate-900">{integration.nextSync}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Latency</div>
                  <div className="text-sm font-medium text-slate-900">{integration.latency}</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Error Rate</div>
                  <div className={`text-sm font-medium ${integration.errorRate === 0 ? 'text-primary' :
                    integration.errorRate < 1 ? 'text-slate-700' : 'text-slate-900'
                    }`}>
                    {integration.errorRate}%
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Actions Today</div>
                  <div className="text-2xl font-semibold text-slate-900">{integration.actionsToday.toLocaleString()}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{integration.actionsWeek.toLocaleString()} this week</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1">Data Volume</div>
                  <div className="text-2xl font-semibold text-slate-900">{integration.dataVolume}</div>
                  <div className="text-xs text-slate-400 mt-0.5">Sync frequency: {integration.syncFrequency}</div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Features</h3>
                <div className="flex flex-wrap gap-2">
                  {integration.features.map((feature) => (
                    <span key={feature} className="text-xs px-2 py-1 bg-slate-100 text-slate-600 rounded">
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-2">Granted Permissions</h3>
                <div className="space-y-2">
                  {integration.permissions.map((perm) => (
                    <div key={perm} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <Check className="w-4 h-4 text-primary" />
                      <code className="text-xs text-slate-700">{perm}</code>
                    </div>
                  ))}
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-xs text-slate-500">
                  Connected since {integration.connectedAt}. Permissions last reviewed: Never
                </p>
                <Button variant="outline" size="sm" className="mt-2">
                  Review Permissions
                </Button>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 mb-2">Recent sync activity</div>
              {[
                { time: '2 mins ago', action: 'User sync completed', count: 1247 },
                { time: '17 mins ago', action: 'User sync completed', count: 1247 },
                { time: '32 mins ago', action: 'User sync completed', count: 1246 },
                { time: '47 mins ago', action: 'Group sync completed', count: 89 },
                { time: '1 hour ago', action: 'User sync completed', count: 1246 },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 border border-slate-100 rounded-lg">
                  <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-slate-900">{item.action}</div>
                    <div className="text-xs text-slate-500">{item.count.toLocaleString()} records processed</div>
                  </div>
                  <div className="text-xs text-slate-400">{item.time}</div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sync Frequency</label>
                <select className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20">
                  <option value="1m">Every 1 minute</option>
                  <option value="5m">Every 5 minutes</option>
                  <option value="15m" selected>Every 15 minutes</option>
                  <option value="30m">Every 30 minutes</option>
                  <option value="1h">Every hour</option>
                </select>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="rounded border-slate-300" />
                  <span className="text-sm text-slate-700">Send alerts on sync failures</span>
                </label>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <input type="checkbox" defaultChecked className="rounded border-slate-300" />
                  <span className="text-sm text-slate-700">Include in daily health report</span>
                </label>
              </div>
              <div className="pt-4 border-t border-slate-200">
                <Button variant="outline" size="sm" className="text-slate-600">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Open in {integration.name}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
          <div className="flex items-center gap-2">
            {integration.paused ? (
              <AsyncButton
                variant="outline"
                size="sm"
                onClick={() => onResume(integration.id)}
                loadingText="Resuming..."
                successMessage="Integration resumed"
              >
                <Play className="w-4 h-4 mr-1" />
                Resume Sync
              </AsyncButton>
            ) : (
              <AsyncButton
                variant="ghost"
                size="sm"
                className="text-slate-500"
                onClick={() => onPause(integration.id)}
                loadingText="Pausing..."
                successMessage="Integration paused"
              >
                <Pause className="w-4 h-4 mr-1" />
                Pause Sync
              </AsyncButton>
            )}
            <AsyncButton
              variant="outline"
              size="sm"
              onClick={() => onTestConnection(integration.id)}
              loadingText="Testing..."
            >
              <TestTube className="w-4 h-4 mr-1" />
              Test Connection
            </AsyncButton>
          </div>
          <div className="flex items-center gap-2">
            <AsyncButton
              variant="outline"
              size="sm"
              onClick={() => onSyncNow(integration.id)}
              loadingText="Syncing..."
              successMessage="Sync started"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Sync Now
            </AsyncButton>
            <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Connectors = () => {
  const [integrations, setIntegrations] = useState(mockIntegrations);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const filteredIntegrations = useMemo(() => {
    return integrations.filter(int => {
      if (statusFilter !== 'all' && int.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && int.category !== categoryFilter) return false;
      if (searchQuery && !int.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      return true;
    });
  }, [integrations, statusFilter, categoryFilter, searchQuery]);

  const stats = useMemo(() => ({
    total: integrations.length,
    healthy: integrations.filter(i => i.status === 'healthy').length,
    degraded: integrations.filter(i => i.status === 'degraded').length,
    error: integrations.filter(i => i.status === 'error').length,
    paused: integrations.filter(i => i.status === 'paused').length,
    totalActionsToday: integrations.reduce((acc, i) => acc + i.actionsToday, 0),
    avgLatency: Math.round(
      integrations.filter(i => i.latency !== '-').reduce((acc, i) => acc + parseInt(i.latency), 0) /
      integrations.filter(i => i.latency !== '-').length
    )
  }), [integrations]);

  const categories = useMemo(() =>
    [...new Set(integrations.map(i => i.category))],
    [integrations]
  );

  const handlePause = async (id) => {
    await new Promise(resolve => setTimeout(resolve, 800));
    setIntegrations(integrations.map(i =>
      i.id === id ? { ...i, status: 'paused', paused: true, pausedReason: 'Manually paused' } : i
    ));
    setSelectedIntegration(prev => prev?.id === id ? { ...prev, status: 'paused', paused: true } : prev);
  };

  const handleResume = async (id) => {
    await new Promise(resolve => setTimeout(resolve, 800));
    setIntegrations(integrations.map(i =>
      i.id === id ? { ...i, status: 'healthy', paused: false, pausedReason: null } : i
    ));
    setSelectedIntegration(prev => prev?.id === id ? { ...prev, status: 'healthy', paused: false } : prev);
  };

  const handleReconnect = async (id) => {
    toast.info('Reconnection initiated', { description: 'Attempting to reconnect...' });
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIntegrations(integrations.map(i =>
      i.id === id ? { ...i, status: 'healthy', alert: null, lastSync: 'just now' } : i
    ));
    toast.success('Reconnected successfully');
    setSelectedIntegration(null);
  };

  const handleSyncNow = async (id) => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    const integration = integrations.find(i => i.id === id);
    setIntegrations(integrations.map(i =>
      i.id === id ? { ...i, lastSync: 'just now', nextSync: `in ${i.syncFrequency}` } : i
    ));
    toast.success('Sync completed', { description: `${integration?.name} data synced successfully` });
  };

  const handleTestConnection = async (id) => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    const integration = integrations.find(i => i.id === id);
    if (integration?.status === 'error') {
      toast.error('Connection test failed', { description: 'Unable to reach the API endpoint' });
    } else {
      toast.success('Connection test passed', {
        description: `${integration?.name} API is reachable (${integration?.latency} latency)`
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Database className="w-5 h-5 text-slate-500" />
            Integrations
          </h1>
          <p className="text-sm text-slate-500 mt-1">Monitor and manage connected systems</p>
        </div>
        <Button size="sm">
          <Cloud className="w-4 h-4 mr-1" />
          Add Integration
        </Button>
      </div>

      {(stats.error > 0 || stats.degraded > 0) && (
        <div className="bg-slate-100 border border-slate-300 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-slate-700 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-slate-900">
                {stats.error > 0 && `${stats.error} integration${stats.error > 1 ? 's' : ''} with errors`}
                {stats.error > 0 && stats.degraded > 0 && ', '}
                {stats.degraded > 0 && `${stats.degraded} degraded`}
              </h3>
              <p className="text-sm text-slate-600 mt-0.5">
                Review affected integrations to ensure data sync is working correctly.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Total</span>
            <Database className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{stats.total}</div>
          <div className="text-xs text-slate-400 mt-1">integrations</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Healthy</span>
            <Check className="w-4 h-4 text-primary" />
          </div>
          <div className="text-2xl font-semibold text-primary">{stats.healthy}</div>
          <div className="text-xs text-slate-400 mt-1">operational</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Issues</span>
            <AlertTriangle className="w-4 h-4 text-slate-600" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{stats.error + stats.degraded}</div>
          <div className="text-xs text-slate-400 mt-1">need attention</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Actions Today</span>
            <Zap className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{stats.totalActionsToday.toLocaleString()}</div>
          <div className="text-xs text-primary flex items-center gap-1 mt-1">
            <TrendingUp className="w-3 h-3" />
            +12% vs yesterday
          </div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-500">Avg Latency</span>
            <Clock className="w-4 h-4 text-slate-400" />
          </div>
          <div className="text-2xl font-semibold text-slate-900">{stats.avgLatency}ms</div>
          <div className="text-xs text-slate-400 mt-1">response time</div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search integrations..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          {['all', 'healthy', 'degraded', 'error', 'paused'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-2 py-1 text-xs font-medium rounded capitalize transition-colors ${statusFilter === status ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
            >
              {status === 'all' ? 'All' : statusConfig[status]?.label || status}
            </button>
          ))}
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20"
        >
          <option value="all">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <div className="divide-y divide-slate-100">
          {filteredIntegrations.length === 0 ? (
            <div className="px-4 py-8 text-center text-slate-500 text-sm">
              No integrations match your filters
            </div>
          ) : (
            filteredIntegrations.map((integration) => {
              const StatusIcon = statusConfig[integration.status]?.icon || Check;
              const logo = integration.logoId ? getLogoByIntegrationId(integration.logoId) : null;
              return (
                <div
                  key={integration.id}
                  className="flex items-center gap-4 px-4 py-4 hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedIntegration(integration)}
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    {logo ? (
                      <img src={logo.src} alt={logo.alt} className="w-6 h-6 object-contain" />
                    ) : (
                      <integration.icon className="w-5 h-5 text-slate-600" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-900">{integration.name}</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded flex items-center gap-1 ${statusConfig[integration.status]?.className}`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig[integration.status]?.label}
                      </span>
                      {integration.alert && (
                        <AlertTriangle className="w-3.5 h-3.5 text-slate-600" />
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-slate-500">{integration.category}</span>
                      <span className="text-xs text-slate-400">Last sync: {integration.lastSync}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-medium text-slate-900">{integration.actionsToday.toLocaleString()}</div>
                    <div className="text-xs text-slate-400">actions today</div>
                  </div>
                  <div className="text-right shrink-0 w-16">
                    <div className={`text-sm font-medium ${integration.latency === '-' ? 'text-slate-400' :
                      parseInt(integration.latency) < 100 ? 'text-primary' :
                        parseInt(integration.latency) < 300 ? 'text-slate-700' : 'text-slate-900'
                      }`}>
                      {integration.latency}
                    </div>
                    <div className="text-xs text-slate-400">latency</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              );
            })
          )}
        </div>
      </div>

      <IntegrationDetailModal
        integration={selectedIntegration}
        onClose={() => setSelectedIntegration(null)}
        onPause={handlePause}
        onResume={handleResume}
        onReconnect={handleReconnect}
        onSyncNow={handleSyncNow}
        onTestConnection={handleTestConnection}
      />
    </div>
  );
};

export default Connectors;
